import React, { useMemo } from "react";
import { Form } from "react-bootstrap";
import EmployeePermissionPanel from "../../pages/EmployeePermissionPanel";
import {
  PERMISSIONS,
  permissionBoolMapToCodes,
  permissionCodesToBoolMap,
  resolveErpPermissionCode,
} from "../../utils/permissions";

const EMPTY_BASE = (() => {
  const base = {};
  Object.values(PERMISSIONS).forEach((slug) => {
    base[slug] = false;
  });
  return base;
})();

const KNOWN_CODES = new Set(
  Object.values(PERMISSIONS)
    .map((slug) => resolveErpPermissionCode(slug))
    .filter(Boolean),
);

function humanizeCode(code) {
  return String(code || "")
    .toLowerCase()
    .split("_")
    .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : ""))
    .join(" ");
}

/**
 * Reusable checklist over ERP `permission_codes` (string[]).
 * Keeps EmployeePermissionPanel UI; merges API catalog into an "Other" group.
 */
function PermissionChecklist({
  value = [],
  onChange,
  roleDefaultCodes = [],
  catalogCodes = [],
  disabled = false,
  square = false,
  showRoleHint = false,
  compact = false,
}) {
  const selectedCodes = useMemo(
    () =>
      (Array.isArray(value) ? value : []).map((c) => String(c).toUpperCase()),
    [value],
  );

  const roleDefaults = useMemo(
    () =>
      new Set(
        (Array.isArray(roleDefaultCodes) ? roleDefaultCodes : []).map((c) =>
          String(c).toUpperCase(),
        ),
      ),
    [roleDefaultCodes],
  );

  const panelPermissions = useMemo(
    () => permissionCodesToBoolMap(selectedCodes, EMPTY_BASE),
    [selectedCodes],
  );

  const otherCodes = useMemo(() => {
    const fromCatalog = (Array.isArray(catalogCodes) ? catalogCodes : []).map(
      (c) => String(c).toUpperCase(),
    );
    const extras = new Set([
      ...fromCatalog.filter((c) => c && !KNOWN_CODES.has(c)),
      ...selectedCodes.filter((c) => c && !KNOWN_CODES.has(c)),
    ]);
    return Array.from(extras).sort();
  }, [catalogCodes, selectedCodes]);

  const handlePanelChange = (nextBoolMap) => {
    if (typeof onChange !== "function") return;
    const fromPanel = permissionBoolMapToCodes(nextBoolMap);
    const otherSelected = selectedCodes.filter((c) => !KNOWN_CODES.has(c));
    onChange(Array.from(new Set([...fromPanel, ...otherSelected])));
  };

  const toggleOther = (code, on) => {
    if (typeof onChange !== "function" || disabled) return;
    const set = new Set(selectedCodes);
    if (on) set.add(code);
    else set.delete(code);
    onChange(Array.from(set));
  };

  const extraCount = selectedCodes.filter((c) => !roleDefaults.has(c)).length;
  const revokedCount = [...roleDefaults].filter(
    (c) => !selectedCodes.includes(c),
  ).length;

  return (
    <div>
      {showRoleHint && roleDefaults.size > 0 && (
        <p className="text-muted small mb-2">
          Role defaults: {roleDefaults.size}
          {extraCount > 0 ? ` · +${extraCount} extra` : ""}
          {revokedCount > 0 ? ` · −${revokedCount} removed` : ""}
        </p>
      )}
      <EmployeePermissionPanel
        permissions={panelPermissions}
        disabled={disabled}
        square={square}
        onChange={handlePanelChange}
      />
      {otherCodes.length > 0 && (
        <div className={`mt-3 border-top pt-3 ${compact ? "" : ""}`}>
          <h6 className="fw-semibold mb-2">Other</h6>
          <div className="row g-2">
            {otherCodes.map((code) => (
              <div className="col-md-4 col-sm-6" key={code}>
                <Form.Check
                  type="checkbox"
                  id={`perm-other-${code}`}
                  label={`${humanizeCode(code)} (${code})`}
                  checked={selectedCodes.includes(code)}
                  disabled={disabled}
                  onChange={(e) => toggleOther(code, e.target.checked)}
                />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default PermissionChecklist;
